import { z } from "zod";

/**
 * Customers validation (Phase 5.2).
 *
 * Boundary validation for the create / update / bulk / email flows. The web and
 * API sides validate identically; the API remains authoritative. Rules mirror
 * the legacy client-side checks in `web/js/customers.js`:
 *   • Customer ID is always required.
 *   • Name fields are required when included (create, or edit with Name Change).
 *   • A service that offers options must have one selected.
 */

export const CUSTOMER_UPSERT_MODES = ["create", "update"] as const;
export const customerUpsertModeSchema = z.enum(CUSTOMER_UPSERT_MODES);

/** True when a service id/name is the HUDU source of truth (never provisioned). */
export function isHuduService(svc: { id?: unknown; name?: unknown }): boolean {
  const id = String(svc?.id ?? "").toLowerCase();
  const name = String(svc?.name ?? "").toLowerCase();
  return id === "hudu" || name === "hudu";
}

export const customerSelectedServiceSchema = z.object({
  id: z.string().trim().min(1, "Service id is required"),
  name: z.string().trim().min(1, "Service name is required"),
  option: z.string().trim().min(1).optional(),
});

export type CustomerSelectedServiceInput = z.infer<
  typeof customerSelectedServiceSchema
>;

const optionalTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined));

/**
 * POST /api/v1/customers/upsert. `nameChange`/`addService` are the legacy
 * edit-mode flags forwarded to n8n. When names are provided they must be
 * non-empty; the service layer performs the same check for the current mode.
 */
export const upsertCustomerSchema = z
  .object({
    mode: customerUpsertModeSchema,
    customerId: z.string().trim().min(1, "Customer ID is required").max(100),
    hebrewName: optionalTrimmed(200),
    englishName: optionalTrimmed(200),
    services: z.array(customerSelectedServiceSchema).max(100).optional(),
    nameChange: z.boolean().optional(),
    addService: z.boolean().optional(),
    original: z
      .object({
        customerId: z.string().trim().min(1).max(100),
        hebrewName: optionalTrimmed(200),
        englishName: optionalTrimmed(200),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    // Create always needs both names; update needs them only when the caller
    // signals a name change (or is not an edit at all).
    const requiresNames = data.mode === "create" || data.nameChange === true;
    if (requiresNames) {
      if (!data.hebrewName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["hebrewName"],
          message: "Hebrew name is required",
        });
      }
      if (!data.englishName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["englishName"],
          message: "English name is required",
        });
      }
    }
  });

export type UpsertCustomerInput = z.infer<typeof upsertCustomerSchema>;

/** POST /api/v1/customers/bulk-upsert. */
export const bulkUpsertCustomerSchema = z.object({
  customerIds: z
    .array(z.string().trim().min(1))
    .min(1, "At least one Customer ID is required")
    .max(1000),
  services: z
    .array(customerSelectedServiceSchema)
    .min(1, "Select at least one service to apply"),
});

export type BulkUpsertCustomerInput = z.infer<typeof bulkUpsertCustomerSchema>;

/**
 * POST /api/v1/customers/jobs/:jobId/result — the n8n provisioning callback.
 * Authenticated out-of-band by the shared `x-techform-secret` header. The
 * result object is freeform (the script's stdout), so it is left unconstrained.
 */
export const customerJobResultSchema = z.object({
  ok: z.boolean(),
  result: z.record(z.unknown()).nullable().optional(),
  // n8n builds the body from expressions, so an absent error arrives as an
  // explicit `null` rather than a missing key. Rejecting that failed the whole
  // callback with a 400 ("Expected string, received null") and left the job
  // stuck "running" even on a successful provisioning run. Accept null and
  // normalize it to undefined so downstream code keeps a single empty case.
  error: z
    .string()
    .max(2000)
    .nullish()
    .transform((v) => v ?? undefined),
});

export type CustomerJobResultInput = z.infer<typeof customerJobResultSchema>;

/** POST /api/v1/customers/email-result. */
export const emailCustomerResultSchema = z
  .object({
    to: z
      .array(z.string().trim().min(1))
      .min(1, "At least one recipient is required")
      .max(50),
    subject: z.string().trim().min(1, "Subject is required").max(200),
    html: z.string().optional(),
    text: z.string().optional(),
  })
  .refine((d) => Boolean(d.html) || Boolean(d.text), {
    message: "An html or text body is required",
    path: ["html"],
  });

export type EmailCustomerResultInput = z.infer<
  typeof emailCustomerResultSchema
>;
